/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreHandlerValues — the lenient value coercers (nz / nb / n0 / kNotNullOrUndefined / kNotEmptyString /
 * roundToTwo) shared by kCoreHandler and its split parts (kCoreHandlerText, kCoreHandlerUtil).
 *
 * A LEAF module (imports nothing), so the split parts no longer import kCoreHandler at runtime (import
 * cycle removed 2026-10-22). kCoreHandler re-exports every name here, so an import of nz from kCoreHandler
 * is unchanged.
 */

// Server-safe equivalents of the ks helper methods. These hold the richer
// behaviour that previously lived only on the browser `ks` class (consolidated
// here so kCoreTime / kCoreObjects and the `kCore` base class below all share
// one implementation). They are pure and deterministic, so per the documented
// carve-out in the coding-typescript skill they omit the KErrorMode guard.

/**
 * True when v is neither null nor undefined, AND is not the literal strings
 * "null" / "undefined" (which SharePoint and query strings emit as text).
 */
export const kNotNullOrUndefined = (v: any): boolean => {
    if (v === null) { return false; }
    if (v === undefined) { return false; }
    if (v === "undefined") { return false; }
    if (v === "null") { return false; }
    return true;
};

/** Returns the string value of v, or "" when v is null/undefined. */
export const nz = (v: any): string => {
    if (!kNotNullOrUndefined(v)) { return ""; }
    if (typeof v === "number") { return (v as number).toString(); }
    return v;
};

/** Returns the boolean value of v, or false when v is null/undefined. */
export const nb = (v: any): boolean => {
    if (kNotNullOrUndefined(v)) { return Boolean(v); }
    if (v === true) { return true; }
    if (v === "true") { return true; }
    return false;
};

/** Returns the numeric value of v, or 0 when v is null/undefined. Strips spaces
 *  first so thousand-separated strings ("1 234") parse to a number. */
export const n0 = (v: any): number => {
    if (!kNotNullOrUndefined(v)) { return 0; }
    let Local: any = v;
    if (typeof v === "string") { Local = v.split(" ").join(""); }
    const RetVal: number = parseFloat(Local);
    if (isNaN(RetVal)) { return 0; }
    return RetVal;
};

/** True when v is a non-empty string (a lone zero-width space U+200B counts as empty). */
export const kNotEmptyString = (v: any): boolean => {
    if (!kNotNullOrUndefined(v)) { return false; }
    if (v === "") { return false; }
    if (v.length === 1 && typeof v === "string" && v.charCodeAt(0) === 8203) { return false; }
    return true;
};

/** Round a number to at most two decimal places (handles negatives explicitly). */
export const roundToTwo = (v: number): number => {
    let Negative = false;
    let Num: number = v;
    if (Num < 0) { Negative = true; Num = Num * -1; }
    const Multiplicator: number = Math.pow(10, 2);
    Num = parseFloat((Num * Multiplicator).toFixed(11));
    Num = parseFloat((Math.round(Num) / Multiplicator).toFixed(2));
    if (Negative) { Num = parseFloat((Num * -1).toFixed(2)); }
    return Num;
};

